import React, { FC } from "react";

import { toast } from "react-toastify";

import {
  useApisManagersSalesConceptCategoriesDestroy,
  useSearchManagersSalesConceptCategories,
} from "~/hooks";

import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
} from "~/components/layouts";
import {
  MultiParentsWithMultiChildrenDropdownField,
  FilterWrapper,
  GridContainer,
} from "~/components/organisms";

import { SalesConceptCategoryType } from "~/domains";

import {
  GridRow,
  OpenedMenuInGrid,
  LabelWithTextField,
  LabelWithDropDownField,
  InfiniteScrollWithMoreButton,
  LabelWithMultipleDropdownField,
} from "~/ui";
import {
  FormSubmitButton,
  Form,
  Button,
  GridHeaderCells,
  BooleanDropDownField,
} from "~/ui";

export const ManagersSalesConceptCategoriesIndex: FC = () => {
  const {
    salesConceptCategories,
    keyword,
    isLoading,
    allBusinessSectionsDistribution,
    hasNextPage,
    selectableDropdownTags,
    selectedDropdownTags,
    selectableDropdownPublicStatusIds,
    selectedDropdownPublicStatusId,
    selectableDropdownDivisions,
    selectedDropdownDivisions,
    selectableDropdownSections,
    selectedDropdownSections,
    onDivisionChange,
    onSectionChange,
    onChangeDropdownTags,
    findAndRemove,
    onChangeDropdownPublicStatusId,
    fetchNextPage,
    onChangeKeyword,
    onConditionReset,
    onSearchSubmit,
    setAllBusinessSectionsDistribution,
  } = useSearchManagersSalesConceptCategories();
  const { mutate: deleteRequest } =
    useApisManagersSalesConceptCategoriesDestroy();

  const handleDelete = (salesConceptCategory: SalesConceptCategoryType) => {
    const isConfirm = confirm("本当に削除しますか？");
    if (!isConfirm) return;
    deleteRequest(
      {
        id: salesConceptCategory.id,
      },
      {
        onSuccess: (data) => {
          toast(data.message);
          findAndRemove((item) => item.id === salesConceptCategory.id);
        },
      },
    );
  };

  const generateSalesConceptCategoryLists = (
    salesConceptCategory: SalesConceptCategoryType,
  ) => {
    return [
      salesConceptCategory.name,
      salesConceptCategory.allBusinessSectionsDistribution
        ? "すべての部署"
        : salesConceptCategory.businessDivisions
            .map((division) => division.name)
            .join(","),
      salesConceptCategory.allBusinessSectionsDistribution
        ? "すべての課"
        : salesConceptCategory.businessSections
            .map((section) => section.name)
            .join(","),
      salesConceptCategory.publicStatus.name,
    ];
  };

  const breadCrumbItems = [
    { label: "ブログ", href: "/managers/sales_concept_categories" },
    { label: "一覧", href: "/managers/sales_concept_categories" },
  ];

  return (
    <EmployeeLayout withRightSidebar={false} breadCrumbItems={breadCrumbItems}>
      <EmployeeLayoutMainContent withRightSidebar={false}>
        <FilterWrapper title="ブログ検索">
          <Form onSubmit={onSearchSubmit} className="space-y-6">
            <div className="grid miniTablet:grid-cols-2 gap-4">
              <MultiParentsWithMultiChildrenDropdownField
                allParents={selectableDropdownDivisions}
                parentsValue={selectedDropdownDivisions}
                parentLabel="部署"
                parentsOnChange={onDivisionChange}
                allChildren={selectableDropdownSections}
                childrenValue={selectedDropdownSections}
                childLabel="課"
                childrenOnChange={onSectionChange}
              />
              <BooleanDropDownField
                name="isAllBusinessSections"
                trueLabel="すべての課に配布する"
                falseLabel="個別の課に配布する"
                labelText="配布種別の選択"
                value={allBusinessSectionsDistribution}
                onChange={setAllBusinessSectionsDistribution}
              />
              <LabelWithMultipleDropdownField
                labelText="タグ"
                name="publicStatus"
                options={selectableDropdownTags}
                value={selectedDropdownTags}
                onChange={onChangeDropdownTags}
              />
              <LabelWithDropDownField
                labelText="ステータス"
                name="publicStatus"
                options={selectableDropdownPublicStatusIds}
                value={selectedDropdownPublicStatusId}
                onChange={onChangeDropdownPublicStatusId}
              />
              <LabelWithTextField
                labelText="フリーワード"
                type="search"
                name="keyword"
                placeholder="フリーワード検索"
                value={keyword}
                onChange={onChangeKeyword}
              />
            </div>
            <div className="flex items-center justify-end space-x-4">
              <Button
                color="gray"
                outline
                className="w-full miniTablet:w-auto"
                text="リセット"
                onClick={onConditionReset}
              />
              <FormSubmitButton
                value="検索する"
                color="primary"
                className="w-full miniTablet:w-auto"
              />
            </div>
          </Form>
        </FilterWrapper>
        <InfiniteScrollWithMoreButton
          itemsLength={salesConceptCategories.length}
          nextFetchFunction={fetchNextPage}
          isLoading={isLoading}
          hasMore={hasNextPage}
          className="mt-9"
        >
          <GridContainer cols={4}>
            <GridHeaderCells texts={["タイトル", "部署", "課", "ステータス"]} />
            {salesConceptCategories.map((salesConceptCategory) => (
              <GridRow
                key={salesConceptCategory.id}
                lists={generateSalesConceptCategoryLists(salesConceptCategory)}
                openedMenu={
                  <OpenedMenuInGrid
                    cols={4}
                    buttons={[
                      {
                        text: "編集",
                        link: `/managers/sales_concept_categories/${salesConceptCategory.id}/edit`,
                      },
                      {
                        text: "削除",
                        onClick: () => handleDelete(salesConceptCategory),
                      },
                    ]}
                  />
                }
              />
            ))}
          </GridContainer>
        </InfiniteScrollWithMoreButton>
      </EmployeeLayoutMainContent>
    </EmployeeLayout>
  );
};
